import React, { useCallback, useMemo } from "react";
import { useLazyLoadQuery, graphql } from "react-relay";
import StatTable from "../../../../../StatTable";
import ScrollShadow from "../../__shared/NFLTable";
import ToolStatePanel from "../../__shared/ToolStatePanel";
import { toLeagueRows } from "../scoutingSeries";

const LEAGUE_STATS = graphql`
  query LeagueStatsDisplayQuery($seasonID: ID) {
    nflScoutingReportTeamStats(seasonId: $seasonID) {
      team {
        id
        abbreviation
      }

      playsPerGame

      yardsPerPlay
      passYdsPerAtt
      rushYdsPerAtt

      oppYardsPerPlay
      oppPassYdsPerAtt
      oppRushYdsPerAtt
    }
  }
`;

const COLUMNS = [
  { Header: "Team", accessor: "teamAbbr", sortDescFirst: false },
  { Header: "Plays", accessor: "playsPerGame" },
  { Header: "Yards Per Play", accessor: "yardsPerPlay" },
  { Header: "Pass Yards Per Att", accessor: "passYdsPerAtt" },
  { Header: "Rush Yards Per Att", accessor: "rushYdsPerAtt" },
  { Header: "Opp Yards Per Play", accessor: "oppYardsPerPlay" },
  { Header: "Opp Pass Yards Per Att", accessor: "oppPassYdsPerAtt" },
  { Header: "Opp Rush Yards Per Att", accessor: "oppRushYdsPerAtt" },
];

const INITIAL_STATE = { sortBy: [{ id: "yardsPerPlay", desc: true }] };

export default function LeagueStatsDisplay({ teamId, seasonID, fetchKey }) {
  const queryData = useLazyLoadQuery(
    LEAGUE_STATS,
    { seasonID },
    { fetchKey, fetchPolicy: fetchKey > 0 ? "network-only" : "store-or-network" }
  );

  if (!queryData.nflScoutingReportTeamStats) {
    throw new Error("nflScoutingReportTeamStats returned null");
  }

  const rows = useMemo(
    () => toLeagueRows(queryData.nflScoutingReportTeamStats),
    [queryData]
  );

  const getRowProps = useCallback(
    (row) =>
      row.original.teamId === teamId ? { className: "nfl-sr-row-selected" } : {},
    [teamId]
  );

  if (rows.length === 0) {
    return (
      <ToolStatePanel
        title="No league stats yet"
        body="League comparisons show up after week 1."
      />
    );
  }

  return (
    <ScrollShadow>
      <StatTable
        columns={COLUMNS}
        data={rows}
        initialState={INITIAL_STATE}
        getRowProps={getRowProps}
        interactiveCells={false}
        autoResetSortBy={false}
      />
    </ScrollShadow>
  );
}
